import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gift, Loader2, Send, Clock, Check, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { useCreateAccount } from "@/components/DemoGuard";

const input = "w-full bg-[#04060B] border border-[#D4AF37]/20 rounded-xl px-3 py-2.5 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60";
const today = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };
const nice = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const STATUS = {
  scheduled: [Clock, "Scheduled"], sending: [Clock, "Sending"], sent: [MailCheck, "Delivered"], claimed: [Check, "Opened"],
};

function GiftForm({ readings, initialSlug }) {
  const { currency, prices } = useCurrency();
  const [f, setF] = useState({ slug: initialSlug || "natal", recipient_name: "", recipient_email: "", message: "", deliver_on: today() });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ready = f.recipient_name.trim() && /@/.test(f.recipient_email) && f.deliver_on;
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", { ...f, currency, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong. Please try again."); setBusy(false); }
  };
  return (
    <form onSubmit={go} data-testid="gift-form" className="bg-gradient-to-b from-[#1a1230] to-[#0F1523] border border-[#D4AF37]/40 rounded-3xl p-6 space-y-3">
      <label className="block text-xs text-[#A7B0C0] ml-1">Reading
        <select data-testid="gift-slug" value={f.slug} onChange={set("slug")} className={`${input} mt-1`}>
          {readings.map((r) => <option key={r.slug} value={r.slug}>{r.title}</option>)}
        </select>
      </label>
      <input data-testid="gift-recipient-name" className={input} placeholder="Friend's first name" maxLength={40} value={f.recipient_name} onChange={set("recipient_name")} />
      <input data-testid="gift-recipient-email" type="email" className={input} placeholder="Friend's email" value={f.recipient_email} onChange={set("recipient_email")} />
      <textarea data-testid="gift-message" rows={3} maxLength={300} className={input} placeholder="A short message (optional)" value={f.message} onChange={set("message")} />
      <label className="block text-xs text-[#A7B0C0] ml-1">Deliver on (arrives around 9am your time; today sends straight away)
        <input data-testid="gift-date" type="date" min={today()} value={f.deliver_on} onChange={set("deliver_on")} className={`${input} mt-1 [color-scheme:dark]`} />
      </label>
      <p className="text-xs text-[#A7B0C0]">They'll get an email with a link to open their gift. They add their own birth details, so the reading is written for their chart.</p>
      <button data-testid="btn-gift-pay" disabled={busy || !ready}
        className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <><Send size={17} /> Send this gift · {prices.symbol}{prices.reading}</>}
      </button>
    </form>
  );
}

function SentList({ gifts }) {
  if (!gifts.length) return null;
  return (
    <div className="mt-8" data-testid="gift-sent-list">
      <p className="text-[11px] uppercase tracking-[0.3em] gold-text mb-3">Gifts you've sent</p>
      <div className="space-y-2">
        {gifts.map((g) => {
          const [Icon, label] = STATUS[g.status] || STATUS.scheduled;
          return (
            <div key={g.id} data-testid={`gift-row-${g.id.slice(0, 6)}`} className="bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl px-4 py-3 flex items-center gap-3">
              <Gift size={16} className="gold-text shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-base truncate">{g.title} for {g.recipient_name}</p>
                <p className="text-xs text-[#A7B0C0] truncate">{g.recipient_email} · {nice(g.deliver_on)}</p>
              </div>
              <span className="flex items-center gap-1 text-xs gold-text" data-testid={`gift-status-${g.id.slice(0, 6)}`}><Icon size={13} /> {label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Gifts() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const createAccount = useCreateAccount();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/gifts").then(({ data }) => setD(data)).catch(() => setD({ gifts: [], readings: [] })); }, []);
  return (
    <div className="px-5 pt-6 pb-28 fade-up lg:max-w-xl lg:mx-auto" data-testid="gifts-page">
      <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Gift size={11} className="inline -mt-0.5 mr-1" />Gift a reading</p>
      <h1 className="text-4xl font-light mt-1 mb-2">Send the stars to a friend</h1>
      <p className="text-base text-[#A7B0C0] mb-6">Choose a reading and the day it should arrive: a birthday, an anniversary, or just because.</p>
      {user?.is_demo ? (
        <button data-testid="gift-demo-cta" onClick={createAccount} className="w-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors">
          Create your free account to send a gift
        </button>
      ) : !d ? <div className="flex justify-center py-16"><Loader2 className="animate-spin gold-text" size={26} /></div> : (
        <>
          <GiftForm readings={d.readings} initialSlug={params.get("slug")} />
          <SentList gifts={d.gifts} />
        </>
      )}
    </div>
  );
}
